import React, { useEffect, useId, useRef, useState } from 'react';

import { chartColors, SECONDARY_TEXT } from '../utils';

const WALL = 3;

// Each graphic is drawn in its own coordinates (`size`); `frame` is its viewBox,
// which adds room for the labels. `fill` is `up` (bottom to top), `right` (left
// to right; the battery's terminal lies past `right`, so it stays empty) or
// `around` (a ring, clockwise from the top). With `fitWidth`, a graphic takes at
// most `maxWidth` of the room it is given, all of it by default. `levelAbove`
// puts the level over the graphic rather than beside it, so a wide graphic does
// not give up width to it.
export const LEVEL_GRAPHICS = {
    tank: {
        bottom: 98,
        fill: 'up',
        outline: 'M10,2 H50 A8,8 0 0 1 58,10 V90 A8,8 0 0 1 50,98 H10 A8,8 0 0 1 2,90 V10 A8,8 0 0 1 10,2 Z',
        top: 2,
        size: [60, 100],
        frame: [-26, -8, 128, 108]
    },
    // A cylinder lying on its side, seen side on: domed ends, filled bottom to top.
    horizontal_tank: {
        bottom: 54,
        fill: 'up',
        levelAbove: true,
        outline: 'M10,2 H90 A8,26 0 0 1 90,54 H10 A8,26 0 0 1 10,2 Z',
        top: 2,
        size: [100, 56],
        frame: [-22, -12, 126, 72]
    },
    donut: {
        fill: 'around',
        radius: 38,
        thickness: 18,
        size: [100, 100],
        frame: [-20, 0, 140, 110]
    },
    battery: {
        fill: 'right',
        left: 2,
        outline: 'M8,2 H84 A6,6 0 0 1 90,8 V16 H97 V34 H90 V42 A6,6 0 0 1 84,48 H8 A6,6 0 0 1 2,42 V8 A6,6 0 0 1 8,2 Z',
        right: 90,
        size: [100, 50],
        frame: [0, -12, 100, 70],
        // Its labels sit above and below, so less of its frame is margin than the
        // horizontal tank's: this keeps the two bodies about the same size.
        maxWidth: '80%',
        labelSize: 6
    }
};

export const DEFAULT_LEVEL_GRAPHIC = 'tank';

const QUARTERS = [0.25, 0.5, 0.75];

const LABEL_SIZE = 8;

// The labels' size on screen, the same on every graphic. Each graphic is drawn at
// its own scale, so their size in its coordinates is worked out once it is laid
// out; `size` and `labelSize` are only the fallback until then.
const
    LABEL_PX = 9,
    LEVEL_LABEL_PX = 12;

// No space before the sign, unlike formatValue's units.
export const formatPercent = value => `${formatValue(value, '', 0)}%`;

// Coordinate units per screen pixel, or null before the graphic is laid out.
function useUnitsPerPixel(ref, [frameWidth, frameHeight]) {
    const [unitsPerPixel, setUnitsPerPixel] = useState(null);

    useEffect(() => {
        const element = ref.current;

        if (!element) {
            return;
        }

        // The observer's size leaves out transforms, such as a modal's opening
        // animation, and it reports once on observing.
        const observer = new ResizeObserver(([entry]) => {
            const
                { height, width } = entry.contentRect,
                // As preserveAspectRatio "meet" scales it.
                scale = Math.min(width / frameWidth, height / frameHeight);

            setUnitsPerPixel(scale > 0 ? 1 / scale : null);
        });

        observer.observe(element);

        return () => observer.disconnect();
    }, [frameWidth, frameHeight]);

    return unitsPerPixel;
}

function levelLabelPosition(shape, share) {
    const [width, height] = shape.size;

    if (shape.levelAbove) {
        return { anchor: 'middle', baseline: 'auto', size: 9, x: width / 2, y: shape.top - 5 };
    }

    switch (shape.fill) {
        case 'up': {
            const y = shape.bottom - (shape.bottom - shape.top) * share;

            return { anchor: 'start', baseline: 'middle', size: 11, x: width + 4, y: Math.min(shape.bottom - 4, Math.max(shape.top + 4, y)) };
        }
        case 'right': {
            const x = shape.left + (shape.right - shape.left) * share;

            return { anchor: 'middle', baseline: 'auto', size: 9, x: Math.min(shape.right - 8, Math.max(shape.left + 8, x)), y: -3 };
        }
        case 'around':
        default:
            return { anchor: 'middle', baseline: 'middle', size: 14, x: width / 2, y: height / 2 };
    }
}

function labelPositions(shape) {
    const [width, height] = shape.size;

    switch (shape.fill) {
        case 'up': {
            const span = shape.bottom - shape.top;

            return QUARTERS.map(share => ({
                anchor: 'end', baseline: 'middle', share, x: -3, y: shape.bottom - span * share
            }));
        }
        case 'right': {
            const span = shape.right - shape.left;

            return QUARTERS.map(share => ({
                anchor: 'middle', baseline: 'hanging', share, x: shape.left + span * share, y: height + 2
            }));
        }
        case 'around':
        default: {
            const
                cx = width / 2,
                cy = height / 2,
                distance = shape.radius + shape.thickness / 2 + 4;

            return [
                { anchor: 'start', baseline: 'middle', share: 0.25, x: cx + distance, y: cy },
                { anchor: 'middle', baseline: 'hanging', share: 0.5, x: cx, y: cy + distance },
                { anchor: 'end', baseline: 'middle', share: 0.75, x: cx - distance, y: cy }
            ];
        }
    }
}

// Applied through `style`: SVG presentation attributes do not resolve the
// theme's CSS variables.
function levelColors(palette) {
    const
        { dark_green, white } = WiseMetering.baseColors,
        primary = 'rgb(var(--primary))';

    switch (palette) {
        case 'secondary':
            return { fill: SECONDARY_TEXT, tick: dark_green, tickOpacity: 1, wall: 'currentColor' };
        case 'simple':
            return { fill: primary, tick: 'currentColor', tickOpacity: 0.5, wall: primary };
        case 'primary':
        default:
            return { fill: chartColors('primary').series, tick: white, tickOpacity: 1, wall: 'currentColor' };
    }
}

const markProps = colors => ({
    stroke: colors.tick,
    strokeDasharray: '2 3',
    strokeOpacity: colors.tickOpacity,
    strokeWidth: 1
});

const wallProps = colors => ({
    fill: 'none',
    strokeLinejoin: 'round',
    strokeWidth: WALL,
    style: { stroke: colors.wall }
});

function ClippedFill({ clipId, colors, shape, share }) {
    const [width, height] = shape.size;

    let fill, marks;

    if (shape.fill === 'up') {
        const
            span = shape.bottom - shape.top,
            level = span * share;

        fill = <rect height={level} style={{ fill: colors.fill }} width={width} x="0" y={shape.bottom - level}/>;
        marks = QUARTERS.map(quarter => {
            const y = shape.bottom - span * quarter;

            return <line key={quarter} x1="0" x2={width} y1={y} y2={y} {...markProps(colors)}/>;
        });
    } else {
        const
            span = shape.right - shape.left,
            level = span * share;

        fill = <rect height={height} style={{ fill: colors.fill }} width={level} x={shape.left} y="0"/>;
        marks = QUARTERS.map(quarter => {
            const x = shape.left + span * quarter;

            return <line key={quarter} x1={x} x2={x} y1="0" y2={height} {...markProps(colors)}/>;
        });
    }

    return (
        <>
            <clipPath id={clipId}>
                <path d={shape.outline}/>
            </clipPath>
            <g clipPath={`url(#${clipId})`}>
                {fill}
                {marks}
            </g>
            <path d={shape.outline} {...wallProps(colors)}/>
        </>
    );
}

function RingFill({ colors, shape, share }) {
    const
        [width, height] = shape.size,
        cx = width / 2,
        cy = height / 2,
        { radius, thickness } = shape,
        inner = radius - thickness / 2,
        outer = radius + thickness / 2;

    return (
        <>
            {/* `pathLength` makes the dash a percentage of the ring */}
            {share > 0 && (
                <circle
                    cx={cx}
                    cy={cy}
                    fill="none"
                    pathLength="100"
                    r={radius}
                    strokeDasharray={`${share * 100} 100`}
                    strokeWidth={thickness}
                    style={{ stroke: colors.fill }}
                    transform={`rotate(-90 ${cx} ${cy})`}
                />
            )}
            {QUARTERS.map(quarter => {
                const
                    angle = quarter * 2 * Math.PI,
                    sin = Math.sin(angle),
                    cos = -Math.cos(angle);

                return (
                    <line
                        key={quarter}
                        x1={cx + inner * sin}
                        x2={cx + outer * sin}
                        y1={cy + inner * cos}
                        y2={cy + outer * cos}
                        {...markProps(colors)}
                    />
                );
            })}
            <circle cx={cx} cy={cy} r={outer} {...wallProps(colors)}/>
            <circle cx={cx} cy={cy} r={inner} {...wallProps(colors)}/>
        </>
    );
}

export function LevelGraphic({ className, fitWidth = false, graphic, levelLabel, palette, share, style }) {
    const
        shape = LEVEL_GRAPHICS[graphic] || LEVEL_GRAPHICS[DEFAULT_LEVEL_GRAPHIC],
        colors = levelColors(palette),
        [x, y, width, height] = shape.frame,
        clamped = Math.min(1, Math.max(0, share)),
        levelPosition = levelLabel ? levelLabelPosition(shape, clamped) : null,
        // React's ids contain characters that are not safe inside `url(#...)`.
        clipId = `level-${useId().replace(/[^\w-]/g, '')}`,
        svgRef = useRef(null),
        unitsPerPixel = useUnitsPerPixel(svgRef, [width, height]),
        labelSize = unitsPerPixel ? LABEL_PX * unitsPerPixel : shape.labelSize || LABEL_SIZE,
        levelSize = unitsPerPixel ? LEVEL_LABEL_PX * unitsPerPixel : levelPosition?.size;

    return (
        // Labels may run past the frame in a graphic drawn very small.
        <svg
            className={className}
            preserveAspectRatio="xMidYMid meet"
            ref={svgRef}
            style={{ aspectRatio: `${width} / ${height}`, maxWidth: fitWidth ? shape.maxWidth || '100%' : undefined, overflow: 'visible', ...style }}
            viewBox={`${x} ${y} ${width} ${height}`}
        >
            {shape.fill === 'around'
                ? <RingFill colors={colors} shape={shape} share={clamped}/>
                : <ClippedFill clipId={clipId} colors={colors} shape={shape} share={clamped}/>}
            {labelPositions(shape).map(label => (
                <text
                    dominantBaseline={label.baseline}
                    fill="currentColor"
                    fillOpacity="0.8"
                    fontSize={labelSize}
                    key={label.share}
                    textAnchor={label.anchor}
                    x={label.x}
                    y={label.y}
                >
                    {formatPercent(label.share * 100)}
                </text>
            ))}
            {levelPosition && (
                <text
                    dominantBaseline={levelPosition.baseline}
                    fill="currentColor"
                    fontSize={levelSize}
                    fontWeight="bold"
                    textAnchor={levelPosition.anchor}
                    x={levelPosition.x}
                    y={levelPosition.y}
                >
                    {levelLabel}
                </text>
            )}
        </svg>
    );
}
